import { Icon } from '@iconify/react'
import { useState } from 'react'
import { LandingLuckyWheel } from '@/components/LandingLuckyWheel/LandingLuckyWheel'
import type { LandingCampaign, LandingInstitution } from '@/types/landing'
import type {
  LandingRouletteHistoryItem,
  LandingSpinResult,
  LandingRouletteSpinsResponse,
  PublicRouletteDisplay,
} from '@/lib/landing-roulette'
import { formatDateTime, formatRouletteHistoryPrize } from './landing-profile-format'
import styles from './LandingNumbersPage.module.css'
import purchaseButtonStyles from '@/pages/LandingAccountPage/LandingAccountPage.module.css'

type LandingProfileRouletteProps = {
  campaign: LandingCampaign
  institution: LandingInstitution | null
  donorToken: string
  publicRoulette: PublicRouletteDisplay
  shareUrl: string
  salesOpen: boolean
  isWidgetLoading: boolean
  isLoadingHistory: boolean
  rouletteHistory: LandingRouletteHistoryItem[]
  showOpportunityModal: boolean
  onWin: () => void
  onSpinningChange?: (spinning: boolean) => void
  onBalanceChange?: (balance: LandingRouletteSpinsResponse) => void
  onUseCoupon: (result: LandingSpinResult) => Promise<void>
  onBuyClick?: () => void
  onCloseOpportunityModal: () => void
  onGoToRoulette: () => void
  externalBalance?: LandingRouletteSpinsResponse | null
  externalBalanceError?: string | null
  onRetryBalance?: () => void
}

export function LandingProfileRoulette({
  campaign,
  institution,
  donorToken,
  publicRoulette,
  shareUrl,
  salesOpen,
  isWidgetLoading,
  isLoadingHistory,
  rouletteHistory,
  showOpportunityModal,
  onWin,
  onSpinningChange,
  onBalanceChange,
  onUseCoupon,
  onBuyClick,
  onCloseOpportunityModal,
  onGoToRoulette,
  externalBalance,
  externalBalanceError,
  onRetryBalance,
}: LandingProfileRouletteProps) {
  const [usingCoupon, setUsingCoupon] = useState<number | null>(null)
  const [couponError, setCouponError] = useState('')
  const handleUseCoupon = async (item: LandingRouletteHistoryItem) => {
    if (usingCoupon !== null) return
    setUsingCoupon(item.id)
    setCouponError('')
    try {
      await onUseCoupon({
        position: 0,
        prize_type: 'coupon',
        prize_payload: item.prize_payload,
      })
    } catch {
      setCouponError('Não foi possível abrir a compra com seu cupom. Tente novamente.')
    } finally {
      setUsingCoupon(null)
    }
  }
  return (
    <>
      <div className={styles.stack} id="campanha-roleta">
        <section className={styles.stack} aria-label="Roleta da sorte">
          {externalBalanceError && (
            <div role="alert">
              <p>{externalBalanceError}</p>
              <button type="button" onClick={onRetryBalance}>
                Tentar novamente
              </button>
            </div>
          )}
          <LandingLuckyWheel
            externalBalance={externalBalance}
            externalBalanceError={externalBalanceError}
            campaignId={campaign.id}
            donorToken={donorToken}
            publicDisplay={publicRoulette}
            primaryColor={campaign.primary_color}
            buttonColor={campaign.button_color}
            buttonTextColor={campaign.widget_embed_button_text_color}
            shareUrl={shareUrl}
            shareTitle={campaign.public_share_title}
            shareText={campaign.public_share_description}
            storyInput={institution ? { campaign, institution } : undefined}
            onWin={onWin}
            onSpinningChange={onSpinningChange}
            onBalanceChange={onBalanceChange}
            onUseCoupon={onUseCoupon}
            isCouponCheckoutLoading={isWidgetLoading}
            onBuyClick={salesOpen ? onBuyClick : undefined}
          />
        </section>

        <section className={styles.stack} aria-labelledby="landing-profile-history-title">
          <h2 id="landing-profile-history-title" className={styles.subheading}>
            Meus prêmios da roleta
          </h2>

          {isLoadingHistory && rouletteHistory.length === 0 ? (
            <p className={styles.muted}>Carregando histórico...</p>
          ) : rouletteHistory.length > 0 ? (
            <div className={styles.rouletteHistoryList}>
              {rouletteHistory.map((item) => (
                <div key={item.id} className={styles.rouletteHistoryRow}>
                  <div>
                    <strong
                      className={
                        item.prize_type === 'none'
                          ? `${styles.rouletteHistoryPrize} ${styles.rouletteHistoryPrizeNone}`
                          : styles.rouletteHistoryPrize
                      }
                    >
                      {formatRouletteHistoryPrize(item)}
                    </strong>
                    <span className={styles.rouletteHistoryDate}>
                      {formatDateTime(item.created_at)}
                    </span>
                    {item.prize_type === 'lucky_number' &&
                    item.prize_payload?.lucky_numbers?.length ? (
                      <p>
                        {item.status === 'used'
                          ? 'Resgatado — números ativos: '
                          : 'Aguardando resgate em Meus números: '}
                        {item.prize_payload.lucky_numbers.join(' · ')}
                      </p>
                    ) : null}
                    {item.prize_type === 'coupon' ? (
                      <>
                        <p>
                          {item.status === 'used'
                            ? 'Cupom utilizado'
                            : item.status === 'pending'
                              ? item.prize_payload?.valid_until &&
                                item.prize_payload.valid_until <
                                  new Date().toLocaleDateString('sv-SE')
                                ? 'Cupom vencido'
                                : 'Cupom disponível'
                              : 'Cupom indisponível'}
                          {item.prize_payload?.code
                            ? ` · ${item.prize_payload.code}`
                            : ''}
                        </p>
                        {item.status === 'pending' &&
                        item.prize_payload?.code &&
                        salesOpen &&
                        (!item.prize_payload.valid_until ||
                          item.prize_payload.valid_until >=
                            new Date().toLocaleDateString('sv-SE')) ? (
                          <button
                            type="button"
                            className={purchaseButtonStyles.submitButton}
                            disabled={usingCoupon !== null || isWidgetLoading}
                            onClick={() => void handleUseCoupon(item)}
                          >
                            {usingCoupon === item.id
                              ? 'Aplicando cupom…'
                              : 'Usar cupom nesta compra'}
                          </button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                  <Icon
                    icon={
                      item.prize_type === 'none'
                        ? 'mdi:emoticon-sad-outline'
                        : 'mdi:trophy-outline'
                    }
                    className={styles.rouletteHistoryIcon}
                    aria-hidden
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.muted}>
              Você ainda não tem prêmios registrados nesta campanha. Gire a roleta e tente
              a sorte!
            </p>
          )}
          {couponError ? <p role="alert">{couponError}</p> : null}
        </section>
      </div>

      {showOpportunityModal ? (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalPanel}
            role="dialog"
            aria-labelledby="landing-profile-opportunity-title"
          >
            <div className={styles.modalHeader}>
              <div>
                <h2 id="landing-profile-opportunity-title">Oportunidade de prêmios!</h2>
                <p>Você acaba de liberar a Roleta da Sorte.</p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={onCloseOpportunityModal}
                aria-label="Fechar"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
            <p className={styles.modalText}>
              Compartilhe a campanha com seus amigos. Cada compra paga pode liberar uma
              oportunidade de ganhar giros extras, conforme as regras desta roleta.
            </p>
            <button
              type="button"
              className={purchaseButtonStyles.submitButton}
              onClick={onGoToRoulette}
            >
              Ir para a Roleta
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}
